import { delay } from "./apiRetry.js";
import type { KnownSessionRead, SessionRead } from "./sessionReader.js";

// Boot on the shared session reader (sessionReader.ts), both apps: a timeout
// at boot is not signed out.

const BOOT_RETRY_FIRST_MS = 1_000;
const BOOT_RETRY_MAX_MS = 30_000;

// Waits, then asks again, on a capped backoff, only with the app in the
// foreground. Each read starts in the epoch of its time. Null once `cancelled()`.
async function retryUntilKnown<S>(
  read: () => Promise<SessionRead<S>>,
  cancelled: () => boolean,
  foreground: () => Promise<void>,
): Promise<KnownSessionRead<S> | null> {
  for (let wait = BOOT_RETRY_FIRST_MS; ; ) {
    await delay(wait);
    await foreground();
    if (cancelled()) return null;
    const answer = await read();
    if (cancelled()) return null;
    if (answer.kind !== "unknown") return answer;
    wait = Math.min(wait * 2, BOOT_RETRY_MAX_MS);
  }
}

export type BootSession<S, O> =
  | KnownSessionRead<S>
  // Unknown, with an account known on this device: the app starts with it,
  // offline. `settled` is the retry running behind: none there means Login.
  | { kind: "offline"; owner: O; settled: Promise<KnownSessionRead<S> | null> };

/** Boot: unknown is never signed out. With an account known on this device
 *  (`storedOwner`, no network) the app starts with it, so a gym with no
 *  signal still logs to the queue; without one, the splash retries, only
 *  once `foreground` resolves (app in front). Null once `cancelled()`. */
export async function bootSession<S, O>(
  read: () => Promise<SessionRead<S>>,
  storedOwner: () => O | null,
  cancelled: () => boolean,
  foreground: () => Promise<void> = () => Promise.resolve(),
): Promise<BootSession<S, O> | null> {
  const first = await read();
  if (cancelled()) return null;
  if (first.kind !== "unknown") return first;
  const owner = storedOwner();
  const settled = retryUntilKnown(read, cancelled, foreground);
  return owner === null ? settled : { kind: "offline", owner, settled };
}
